import { useTranslation } from "react-i18next";
import { Button, CircularProgress, FormControl, InputLabel, MenuItem, Select, TextField, Tooltip } from "@mui/material";
import CommonGrid from "../CommonGrid";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { api } from "../../../redux/api";
import DataDepotTable from "./DataDepotTable";
import TableLoading from "../../Save/TableLoading";
import DataDepotFieldsTable from "./DataDepotFieldsTable";
import ErrorModal from "../../ItemProperties/Forms/FieldDataValidation/ErrorModal";
import { getDataDepotObject } from "../../../utils/utilFunctions";
import { treeActions } from "../../../redux/slices/treeSlice";
import LightTooltip from "../../../commonComponents/LightTooltip";

export default function DataDepot({setOption, handleClose}) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { account, user, baseUrl, token } = useSelector((state) => state.queryParams);
  const [ddLoading, setDdLoading] = useState(true);
  const [ddfLoading, setDdfLoading] = useState(false);
  const [dataDepotResponse, setDataDepotResponse] = useState([]);
  const [dataDepotList, setDataDepotList] = useState([]);
  const [dataDepotFieldList, setDataDepotFieldList] = useState([]);
  const [dataDepotSelected, setDataDepotSelected] = useState(false);
  const [actionQueue, setActionQueue] = useState([]);
  const [ddAdded, setDdAdded] = useState(false);
  const [addFieldName, setAddFieldName] = useState('');
  const [addDtDropdown, setAddDtDropdown] = useState('ALPHA');
  const [newFieldErrorText, setNewFieldErrorText] = useState('');
  const [addFieldDisabled, setAddFieldDisabled] = useState(true);
  const [addBtnDisabled, setAddBtnDisabled] = useState(true);
  const [postSaveSelect, setPostSaveSelect] = useState(false);
  const [ddFieldTableOverlayText, setDdFieldTableOverlayText] = useState(t('NO_DATA_DEPOT_SELECTED'))
  const [newCopyDD, setNewCopyDD] = useState(false);
  const [preventDdfFetch, setPreventDdfFetch] = useState(false);
  const [editDdfErrorModalOpen, setEditDdfErrorModalOpen] = useState(false);
  const [editDdfErrorMsg, setEditDdfErrorMsg] = useState(t('BOX_ERR_SAVING_DATA_DEPOT'));

  const emptyDDSelect = () => {
    setDataDepotFieldList([]);
  }
  
  //get data depot list, set state with new arr.
  const fetchDDList = (val, newDD) => {
    setDdLoading(true);
    dispatch(api.endpoints.getDataDepotList.initiate(
      { account: account }, { forceRefetch: true }
    )).then(res => {
      if (res.data.current_state === "SUCCESS") {
        if (Object.keys(res.data?.data?.data_depot_list).length > 0) {
          let orderedList = Object.values(res.data.data.data_depot_list).sort((a, b) => a.localeCompare(b)) || [];
          setDataDepotList([...orderedList, '']);
          setDataDepotResponse(res.data.data_depot_list);
          if (newDD) {
            setDataDepotSelected(val);
            setNewCopyDD(val);
          } else {
            setDataDepotFieldList([]);
          }
        } else {
          setDataDepotList(['']);
          setDataDepotResponse([]);
          setDataDepotSelected(false);
          setDataDepotFieldList([]);
        }
      } else {
        setDataDepotList(['']);
        setDataDepotResponse([]);
        setDataDepotSelected(false);
        setDataDepotFieldList([]);
        // console log for dev info in case of an unexpected err. 
        // This error case will need to be handled in the future.
        console.log('DataDepot list fetch error: ', (res.data || 'no data'));
      }
    }).finally(() => setDdLoading(false))
  }

  const fetchDDFields = (dataDepotName) => {
    setActionQueue([]);
    if (!dataDepotName) return emptyDDSelect();
    if (ddAdded) {
      // This overlay text wont rerender on change. Not sure how to force overlay text change on ag-grid
      // setDdFieldTableOverlayText('Add a new field to save the new Data Depot');
      setDataDepotFieldList([]);
      setDdAdded(false);
      return;
    }
    if (preventDdfFetch) {
      //prevent selection of an invalid row to trigger fetch. empty ddf list and reset catch state
      emptyDDSelect();
      setPreventDdfFetch(false);
      return;
    }
    setDdfLoading(true);
    dispatch(api.endpoints.getDataDepotFieldList.initiate(
      { account: account, dataDepotName }, { forceRefetch: true }
    )).then(res => {
      if (res.data.current_state === "SUCCESS" && res.data?.data?.data_field_list && typeof res.data?.data?.data_field_list === "object") {
        let data = res.data?.data?.data_field_list;
        let arr = []
        Object.keys(data).forEach((o, k) => {
          arr.push({id: k, key: o, field_name: data[o].name, data_type: data[o].data_type, value: data[o].data_type})
        });
        let orderedList = [...arr];
        orderedList.sort((a, b) => a.field_name.localeCompare(b.field_name, undefined, {sensitivity: 'base'}));
        orderedList = [...orderedList].map((o, i) => {
          return {
            id: i, 
            key: o.key, 
            field_name: o.field_name, 
            data_type: o.data_type, 
            value: o.value
          }
        })
        setDataDepotFieldList(orderedList)
      } else {
        setDataDepotFieldList([]);
        //console warn for dev info in case of an unexpected err. This will need to be handled in the future.
        console.warn('DataDepot Field list fetch error: ', (res.data || 'no data'));
      }
    }).finally(() => setDdfLoading(false));
  }

  const handleEditDdfErrorModalClose = () => {
    setEditDdfErrorModalOpen(false);
    fetchDDFields(dataDepotSelected);
  }

  const handleApplyErrors = (errors) => {
    //show modal with generic error msg.
    setEditDdfErrorModalOpen(true);
  }

  async function saveFromConfirmModal(nextNode) {
    await applyHandler();
    setPostSaveSelect(nextNode);
  }

  async function applyHandler() {
    setDdfLoading(true);
    let i = 0;
    let errors = [];
    for (const action of actionQueue) {
      let data = {...action.params, account: account}
      await dispatch(api.endpoints[action.type].initiate(
        data, { forceRefetch: true }
      )).then(res => {
        if (!res.data || res.data.current_state === "ERROR") {
          errors.push(res);
          //console warn for dev info in case of an unexpected err. This will need to be handled in the future.
          console.warn('DataDepot Field edit failure: ', (res, data));
        }
      }).finally(() => {
        //when last request is finished, handle errors if any.
        if (i === actionQueue.length - 1) {
          setDdfLoading(false);
          setActionQueue([]);
          if (errors.length > 0) {
            handleApplyErrors(errors);
          }
        }
        
        i++;
        return;
      });
    }

    //update data depot list after successful apply. this will help updatre dropdown in item properties 'Add to Data Depot' component
    await getDataDepotObject({user, account, baseUrl, token},
      (obj) => {
        dispatch(treeActions.setDataDepotObj(obj));
      }
    );
  }

  const logFieldAction = (action, data) => {
    if (action === 'addField') {
      setActionQueue(prev => [...prev, {
        type: 'addDataDepotField',
        params: {
          depotName: dataDepotSelected,
          fieldName: data.name,
          type: data.datatype
        }
      }])
    }
  }

  const addField = (name, datatype) => {
    //log action
    logFieldAction('addField', {name, datatype});

    //add row to table
    setDataDepotFieldList(prev => [...prev, {
      data_type: datatype,
      field_name: name,
      id: prev.length,
      value: datatype,
      key: false
    }]);

    //reset field name and data type inputs
    setAddFieldName('');
    setAddDtDropdown('ALPHA');
  }

  const validateFieldName = (text, callback, editField) => {
    if (!text || !text.length) return callback('BOX_ERR_EMPTY_DD_FIELD_NAME')
    if (editField) {
      //check same name doesnt exist twice (after edit event).
      if (dataDepotFieldList.filter((a, b) => {
        return a.field_name.toLowerCase() === text.toLowerCase();
      }).length > 1) {
        return callback('FIELD_NAME_ALREADY_EXISTS');
      }
    } else {
      //check same name doesnt already exist
      if (dataDepotFieldList.filter((a, b) => {
        return a.field_name.toLowerCase() === text.toLowerCase();
      }).length) {
        return callback('FIELD_NAME_ALREADY_EXISTS');
      }
    }
    

    //only alphanumeric, underscore, dash
    if (!/^[a-zA-Z0-9-_]+$/.test(text)) {
      return callback('BOX_ERR_INVALID_DD_NAME');
    }

    //char limit 45
    if (text.length > 45) {
      return callback('BOX_ERR_DD_NAME_EXCEED_LIMIT');
    }

    return callback(false);
  }

  const handleAddDtDropdownChange = (e) => {
    setAddDtDropdown(e.target.value);
  }

  const handleAddFieldNameChange = (e) => {
    setAddFieldName(e.target.value);
  }

  const handleAddField = () => {
    validateFieldName(addFieldName, (errText) => {
      if (errText) setNewFieldErrorText(t(errText));
      else {
        addField(addFieldName, addDtDropdown);
        setNewFieldErrorText("");
      }
    })
  }

  useEffect(() => {
    if (addFieldName === "") {
      setNewFieldErrorText("");
      setAddBtnDisabled(true);
    } else {
      validateFieldName(addFieldName, (errText) => {
        if (errText) {
          setNewFieldErrorText(t(errText));
        } else {
          setNewFieldErrorText("");
          setAddBtnDisabled(false);
        }
      })
    }
  }, [addFieldName])

  //on component open, fetch left table list.
  useEffect(() => {
    fetchDDList();
  }, []);

  useEffect(() => {
    if (dataDepotList.includes(dataDepotSelected)) fetchDDFields(dataDepotSelected);
    setAddFieldDisabled(!dataDepotSelected);
  }, [dataDepotSelected]);

  return (
    <div style={{height: "calc(100% - 100px)"}}>
    
      {/* description segment */}
      <div className="font-12 padding-12">
        {t('DATA_DEPOT_DESC')} 
      </div>

      {/* Data Depot table and Field table container */}
      <div style={styles.tablesContainer}>
        {ddLoading ? (
          <>
            <Spinner forceStyles={{width: '300px', height: '250px'}}/>
            <div style={{width: '454px', height: '250px'}}></div>
          </>
        ) : (
          <DataDepotTable 
            dataDepotList={dataDepotList} 
            setDataDepotList={setDataDepotList} 
            dataDepotFieldList={dataDepotFieldList}
            setDataDepotFieldList={setDataDepotFieldList}
            dataDepotSelected={dataDepotSelected}
            setDataDepotSelected={setDataDepotSelected}
            actionQueue={actionQueue}
            setActionQueue={setActionQueue}
            fetchDDFields={fetchDDFields}
            fetchDDList={fetchDDList}
            ddAdded={ddAdded} 
            setDdAdded={setDdAdded}
            saveFromConfirmModal={saveFromConfirmModal}
            postSaveSelect={postSaveSelect}
            setPostSaveSelect={setPostSaveSelect}
            dataDepotResponse={dataDepotResponse}
            setDataDepotResponse={setDataDepotResponse}
            newCopyDD={newCopyDD}
            setNewCopyDD={setNewCopyDD}
            setPreventDdfFetch={setPreventDdfFetch}
          />
        )}
        <div style={styles.rightPanelContainer}>
          <div 
            style={styles.ddFieldTableContainer} 
          >
            {ddfLoading ? (
              <Spinner />
            ) : (
              <DataDepotFieldsTable 
                dataDepotFieldList={dataDepotFieldList} 
                setDataDepotFieldList={setDataDepotFieldList}
                actionQueue={actionQueue} 
                setActionQueue={setActionQueue}
                dataDepotSelected={dataDepotSelected} 
                ddFieldTableOverlayText={ddFieldTableOverlayText}
                validateFieldName={validateFieldName}
                fetchDDFields={fetchDDFields}
              />
            )}
          </div>
          <div className="flex align-center" style={styles.addFieldLabel}>
            {t("BTN_ADD_NEW") + " " + t("TIT_FIELD") + ":"}
          </div>
          <div style={styles.addFieldContainer} className={`${addFieldDisabled ? "op-5" : ""}`}>
            <div style={styles.addFieldNameContainer}>
              { dataDepotSelected ? (
                <TextField
                  id="newDDFieldName" 
                  className="width-100"
                  variant="outlined"
                  size="small"
                  type="text"
                  autoFocus={true}
                  disabled={addFieldDisabled}
                  label={t('LBL_FIELD_NAME')}
                  value={addFieldName}
                  onChange={handleAddFieldNameChange}
                  // error={newFieldErrorText.length > 0}
                  // helperText={newFieldErrorText}
                />
              ) :
                (<LightTooltip
                  title={t('NO_DATA_DEPOT_SELECTED')}
                  arrow
                >
                  <TextField
                    id="newDDFieldName" 
                    className="width-100"
                    variant="outlined"
                    size="small"
                    type="text"
                    // autoFocus={true}
                    disabled={addFieldDisabled}
                    label={t('LBL_FIELD_NAME')}
                  />
                </LightTooltip>)
              }
            </div>
            <div>
              <FormControl variant="outlined" size="small" style={styles.addDtDropdownContainer}>
                <InputLabel id="dtDropdownLabel">{t("DATA_TYPE")}</InputLabel>
                <Select
                  // className={"width-100"}
                  disabled={addFieldDisabled}
                  sx={styles.addDtDropdown}
                  name={"DATATYPE"}
                  value={addDtDropdown}
                  labelId="ftDropdownLabel"
                  label={t("LBL_FLD_TYPE")}
                  id="ftDropdown"
                  onChange={handleAddDtDropdownChange}>
                  {fieldDataTypes.map((obj, key) => (
                    <MenuItem key={key} value={obj.value}>
                      {t(obj.text)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button 
                size='small'
                variant="outlined"
                onClick={handleAddField} 
                disabled={addFieldDisabled || addBtnDisabled} 
                style={styles.addFieldBtn}
              >
                {t("ADD")}
              </Button>
            </div>
          </div>
          {newFieldErrorText.length > 0 ? (
            <div style={styles.newFieldErrorText}>
              {newFieldErrorText}
            </div>
          ) : ''}
        </div>
      </div>

      {/* bottom action buttons container */}
      <div style={styles.buttonsContainer}>
        {/* Copy To hidden until development is started on it */}
        {/* <Button variant="outlined" onClick={() => setOption(6)}>
          {t("COPY_TO")}
        </Button> */}
        {/* this div will placehold for flex spacing until Copy To button enabled */}
        <div></div>

        <div className="flex">
          {(actionQueue.length > 0) &&
            <div style={styles.applyBtnHelperText}>
             {`(${actionQueue.length}) ${t('UNSAVED_CHANGES')}`} 
            </div>
          }
          
          <Button 
            size='small'
            variant="outlined"
            onClick={applyHandler} 
            disabled={actionQueue.length === 0} 
          >
            {t("APPLY")}
          </Button>
          <Button 
            size='small'
            variant="outlined" 
            className="margin-left-8"
            onClick={handleClose}
          >
            {t("CANCEL")}
          </Button>
        </div>
      </div>
    
      <ErrorModal
        errorModal={editDdfErrorModalOpen}
        setErrorModal={setEditDdfErrorModalOpen}
        errorMessage={editDdfErrorMsg}
        handleErrorModal={setEditDdfErrorModalOpen}
        closeHandler={handleEditDdfErrorModalClose}
        OkHandler={handleEditDdfErrorModalClose}
      />
    </div>
  )
}

const styles = {
  tablesContainer: {
    display: "flex",
    width: "calc(100% - 32px)",
    height: "100%",
    overflow: "hidden",
    padding: "0 16px"
  },
  buttonsContainer: {
    display: "flex",
    justifyContent: "space-between",
    width: "calc(100% - 32px)",
    padding: "4px 16px 0"
  },
  loadingCircle: {height: '60px !important', width: '60px !important'},
  loadingText: {fontSize: '16px', fontWeight: '500', marginBottom: '8px'},
  loading: {
    position: 'absolute', 
    display: 'flex', 
    flexDirection: 'column', 
    alignItems: 'center',
    justifyContent: 'center',
    // width: '300px',
    // height: '398px',
    // backgroundColor: '#f9f9f9',
    // border: '1px solid #e2e2e2',
    // borderRadius: '5px'
  },
  ddFieldTableContainer: {
    minHeight: '250px', 
    minWidth: '300px', 
    height: '100%', 
    width: '100%', 
    display: "flex",
    justifyContent: "center",
    flexGrow: '1'
  },
  rightPanelContainer: {
    display: 'flex',
    flexDirection: 'column',
    // height: 'fit-content',
    height: '100%',
    width: '100%',
    marginLeft: '12px',
    overflow: 'hidden'
  },
  addFieldLabel: {
    fontSize: "12px",
    fontWeight: "500",
    color: "rbg(0, 0, 0, 0.75)",
    marginTop: "12px"
  },
  addDtDropdownContainer: {
    marginTop: "10px",
  },
  addDtDropdown: {
    width: "120px",
    height: "34px",
    marginRight: "4px"
  },
  addFieldBtn: {
    marginTop: "10px"
  },
  addFieldContainer: {
    width: "100%",
    display: "flex",
    flexWrap: "wrap",
  },
  addFieldNameContainer: {
    flexGrow: '3',
    marginTop: "10px",
    marginRight: "4px"
  },
  applyBtnHelperText: {
    height: "100%",
    fontSize: "12px",
    display: "flex",
    alignItems: "center",
    marginRight: "4px",
    color: "#666666"
  },
  newFieldErrorText: {
    color: "#d32f2f",
    fontSize: "12px"
  }
}

export function Spinner({forceStyles}) {
  const { t } = useTranslation();
  return (
    <div style={forceStyles ? {...styles.loading, ...forceStyles} : styles.loading} >
        <div style={styles.loadingText}>{t('LOADING_IND')}</div>
        <CircularProgress sx={styles.loadingCircle} color="primary"/>
    </div>
  )
}

const fieldDataTypes = [
  { value: "ALPHA", text: "ALPHA"},
  { value: "INTEGER", text: "INTEGER"},
  { value: "FLOAT", text: "FLOAT"},
  { value: "DATE", text: "DATE"},
];